import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { readImageGeometry, putToPresignedTarget } from '@features/content/_shared/upload';
import { api } from '@shared/api/client';
import { asRows } from '@shared/api/envelope';
import { key } from '@shared/api/query-keys';
import { waitForDerivation } from '@shared/assets/derivation';

import { toWire } from './body';
import type { EditorialPostDetail, PostDraft } from './types';

/**
 * Every write the editorial layer performs, and the invalidation each one owes.
 *
 * **Two writer transitions, one removal, and two reviewer decisions — and the
 * split is the whole feature.** `:submit` and `:withdraw` are the writer's, as
 * is `:delete`; `:publish` and `:reject` belong to a holder of the granted
 * reviewer role who is **not** the post's author, and the owning service refuses
 * them on the caller's own post before any state machine runs. The console
 * renders the two decisions only for a holder — but it never computes the
 * outcome, and a `403` arriving anyway is a real answer rather than a bug.
 *
 * **Nothing here is optimistic and nothing auto-retries.** A moderation
 * decision applied locally and refused server-side would show a reviewer a
 * publication that did not happen.
 *
 * **`confirm` is sent on the one write that destroys something.**
 * `editorial_delete` is `hard`: the post, its links and its review history go,
 * and nothing restores them. It travels in the **body** rather than the query
 * string, because the deployed ingress proxies the decoded path and drops
 * `?args` — a removal gated on a query parameter answers `400` forever there.
 * The console sends it only from a `<ConfirmDialog>` accept handler.
 */

function invalidatePosts(queryClient: QueryClient, postId?: string) {
  void queryClient.invalidateQueries({ queryKey: key('content', 'editorialPosts') });
  if (postId) void queryClient.invalidateQueries({ queryKey: key('content', 'editorialPost', postId) });
  // A decision moves a figure on the engagement band of the console entry.
  void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'engagement') });
}

/**
 * The write payload, and the two keys that are NOT on it.
 *
 * **`authorId` never goes on the wire.** The owning service reads the author off
 * the token — `author_user_id=principal.require_user_id()` — and its request
 * model is `extra="forbid"`, so sending one was a `422` naming
 * `body.authorId EXTRA_FORBIDDEN` rather than a field it ignored. It could not
 * be otherwise: a console that could name any account as the writer of a post
 * would be choosing whose name a rejection is addressed to, and the two-person
 * review rule of §13 turns on the author being the caller.
 *
 * **`state` is absent for the same class of reason** — a new post is always a
 * draft and the service assigns it.
 *
 * The body is `bodyHtml`, spelled by `./body.ts`, which is where the block tree
 * becomes the markup the reader is actually served.
 */
function draftBody(draft: PostDraft) {
  return {
    title: draft.title.trim(),
    tagIds: draft.tagIds,
    relatedItemIds: draft.relatedItemIds,
    ...toWire(draft.body),
  };
}

export function useCreatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    // A new post is always a draft — the owning service assigns the state and
    // no request model carries one, which is why this body names no `state`.
    mutationFn: (draft: PostDraft) =>
      api.post<EditorialPostDetail>('content', '/admin/editorialPosts', draftBody(draft)),
    onSuccess: (post) => invalidatePosts(queryClient, post.id),
  });
}

export function useUpdatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, draft }: { postId: string; draft: PostDraft }) =>
      api.patch<EditorialPostDetail>('content', `/admin/editorialPosts/${postId}`, draftBody(draft)),
    onSuccess: (_post, variables) => invalidatePosts(queryClient, variables.postId),
  });
}

/** The two transitions a writer owns, over one path shape. */
export type WriterTransition = 'submit' | 'withdraw';

export function useWriterTransition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, transition }: { postId: string; transition: WriterTransition }) =>
      api.post<EditorialPostDetail>('content', `/admin/editorialPosts/${postId}:${transition}`),
    onSuccess: (_post, variables) => invalidatePosts(queryClient, variables.postId),
  });
}

/**
 * Destroy one post. **Not a transition** — the row leaves the table.
 *
 * The path keeps the service's `:delete` verb and the confirmation rides in the
 * body, which is the half of the request the deployed ingress passes through
 * untouched. Called only from a confirmation dialog's accept handler.
 */
export function useDeletePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: string) =>
      api.post<{ editorialPostId: string }>('content', `/admin/editorialPosts/${postId}:delete`, {
        confirm: true,
      }),
    // **The deleted post's own key is left alone — neither invalidated nor
    // removed — and both of those were tried against develop.** Either one
    // touches a query the detail route still has mounted at this instant, and an
    // active observer whose data is dropped refetches immediately: the console
    // fired one doomed `GET …/{id}` per delete, answered `404`, and logged it.
    // The route navigates away in its own `onSuccess`, the observer unmounts,
    // and the entry is collected. Only the collections, which really did change,
    // are invalidated.
    onSuccess: () => invalidatePosts(queryClient),
  });
}

/** Approve. Writes the review row, the state, and the publication date. */
export function usePublishPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (postId: string) =>
      api.post<EditorialPostDetail>('content', `/admin/editorialPosts/${postId}:publish`),
    onSuccess: (_post, postId) => invalidatePosts(queryClient, postId),
  });
}

/**
 * Reject. **The reason is required on the wire and required in the dialog**,
 * because it is what the writer reads on the post itself — a rejection without
 * one returns the post to its author to guess what would fix it.
 */
export function useRejectPost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, reason }: { postId: string; reason: string }) =>
      api.post<EditorialPostDetail>('content', `/admin/editorialPosts/${postId}:reject`, {
        reason: reason.trim(),
      }),
    onSuccess: (_post, variables) => invalidatePosts(queryClient, variables.postId),
  });
}

interface ArtworkRegistration {
  artworkId: string;
  uploadTarget: string;
}

/**
 * The post's artwork, in the three steps the `assets` contract has.
 *
 * Register the object and receive a presigned target, PUT the bytes straight at
 * storage, then commit — the same sequence `features/content` already uses for a
 * title's cover, against the same routes, with `EDITORIAL_POST` as the subject
 * (`ArtworkSubjectTypeEnum` carries it alongside `CONTENT_ITEM`).
 *
 * **One upload produces every purpose.** The thumbnail is what an operator
 * chooses; the hero is derived from it by proportional crop unless one is
 * uploaded for it specifically.
 *
 * **A post must exist before it can carry artwork**, because the artwork is
 * registered against a subject id. That is why the create route does not mark
 * the thumbnail required and says the image is attached after the draft is
 * saved: a required field on a surface where it cannot yet be filled is a wall,
 * not a rule.
 *
 * The dimensions are read from the file rather than declared: the service
 * re-reads them out of the stored object at the commit, so a guess would surface
 * as a refusal after the whole file had already been transferred.
 */
/**
 * The purposes `assets` derived for one subject, in the two shapes `content`
 * stores.
 *
 * The crop is queued by `:completeUpload` and runs behind it, so a read taken
 * immediately can legitimately come back empty. An empty answer writes nothing
 * rather than clearing a URL that already stands — the next upload, or the
 * operator's next save, carries it instead.
 */
async function readSubjectArtwork(postId: string): Promise<SubjectArtwork> {
  const rows = asRows<{ purpose: string; url: string }>(
    await api.get('assets', `/artworks?subjectType=EDITORIAL_POST&subjectId=${postId}`),
  );

  const of = (purpose: string) => rows.find((row) => row.purpose === purpose)?.url ?? null;

  return { thumbnailUrl: of('thumbnail'), heroArtworkUrl: of('hero') };
}

interface SubjectArtwork {
  thumbnailUrl: string | null;
  heroArtworkUrl: string | null;
}

/**
 * The same read, given the few seconds the derivation actually takes.
 *
 * **A single immediate read loses the race, and it loses it almost every
 * time.** `:completeUpload` returns as soon as the crop is *queued*; measured
 * on develop, the derived objects appear a second or two later. So the write-
 * back that this poll replaces did exactly what its own guard promised —
 * observed nothing, wrote nothing — and the post kept reporting `null` while
 * both purposes sat in the bucket.
 *
 * The loop itself is shared with every other artwork upload in this console:
 * the cover, a season's poster, an episode's still and a credited person's
 * portrait all wait out the same crop worker on the same bounded window.
 */
async function waitForSubjectArtwork(postId: string, previous: SubjectArtwork): Promise<SubjectArtwork> {
  return waitForDerivation(
    () => readSubjectArtwork(postId),
    (found) =>
      (found.thumbnailUrl !== null && found.thumbnailUrl !== previous.thumbnailUrl) ||
      (found.heroArtworkUrl !== null && found.heroArtworkUrl !== previous.heroArtworkUrl),
  );
}

export function useUploadPostArtwork() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, file }: { postId: string; file: File }) => {
      const geometry = await readImageGeometry(file);

      // **What the post held BEFORE this upload.** Presence alone answers on the
      // first read of a post that already carries a thumbnail, and a replacement
      // then wrote the previous url straight back.
      const previous = await readSubjectArtwork(postId);

      const registration = await api.post<ArtworkRegistration>('assets', '/artworks', {
        subjectType: 'EDITORIAL_POST',
        subjectId: postId,
        declaredContentType: file.type,
        declaredSizeBytes: file.size,
        widthPx: geometry.widthPx,
        heightPx: geometry.heightPx,
      });

      await putToPresignedTarget(registration.uploadTarget, file);
      await api.post<null>('assets', `/artworks/${registration.artworkId}:completeUpload`);

      // **`assets` does not write this back, and nothing else was going to.**
      // A title's cover is `content`'s own column, filled by the owning service
      // when the crop lands; an editorial post's `thumbnail_url` and
      // `hero_artwork_url` are columns this console PATCHes. So the upload
      // chain answered `202 → 200 → 202` on every step, the objects landed in
      // the bucket, and the post still reported `thumbnailUrl: null` — the rail
      // drew its gradient stand-in, and the public blog card had no image at
      // all. Verified end to end on develop before this join existed.
      const purposes = await waitForSubjectArtwork(postId, previous);
      if (purposes.thumbnailUrl !== null || purposes.heroArtworkUrl !== null) {
        await api.patch<unknown>('content', `/admin/editorialPosts/${postId}`, purposes);
      }

      return registration;
    },
    onSuccess: (_data, variables) => invalidatePosts(queryClient, variables.postId),
    // A half-finished transfer must not be replayed silently against a new
    // presigned target.
    retry: false,
  });
}
